import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  FaLightbulb, 
  FaMagic, 
  FaInfoCircle, 
  FaArrowLeft,
  FaArrowRight,
  FaPen,
  FaCheck
} from 'react-icons/fa';
import './CustomPromptInput.css';

const CustomPromptInput = ({
  customPrompt,
  jobTitle,
  company,
  onChange,
  onPrev,
  onNext
}) => {
  const [showExamples, setShowExamples] = useState(false);
  const [activatedExample, setActivatedExample] = useState(null);
  
  // Example prompts
  const promptExamples = [
    {
      id: 1,
      title: "Highlight leadership skills",
      description: "Emphasize management and leadership experience",
      text: "Please highlight my leadership and management experience. Focus on team coordination, decision-making, and project leadership aspects of my background."
    },
    {
      id: 2,
      title: "Technical focus",
      description: "Emphasize technical skills and problem-solving abilities",
      text: "Please emphasize my technical skills and problem-solving abilities. Focus on complex challenges I've overcome and innovative solutions I've implemented."
    },
    {
      id: 3,
      title: "Career transition",
      description: "Highlight transferable skills for a career change",
      text: "I'm transitioning to a new field. Please highlight transferable skills from my background that would be relevant to this position, even if from different industries."
    },
    {
      id: 4,
      title: "Entry-level position",
      description: "Emphasize education and potential for recent graduates",
      text: "As a recent graduate, please emphasize my education, coursework, and projects that demonstrate my potential and readiness for this position despite limited work experience."
    }
  ];
  
  // Handle applying an example
  const applyExample = (example) => {
    onChange('customPrompt', example.text);
    setActivatedExample(example.id);
    
    // Reset after 3 seconds
    setTimeout(() => {
      setActivatedExample(null);
    }, 3000);
  };
  
  // Handle text change
  const handleTextChange = (e) => {
    onChange('customPrompt', e.target.value);
  };
  
  return (
    <div className="custom-prompt-input">
      <div className="section-description">
        <h2>Add Custom Instructions</h2>
        <p>
          Provide any specific instructions or emphasis you'd like for tailoring
          {jobTitle && company ? ` your resume for the ${jobTitle} position at ${company}` : 
           jobTitle ? ` your resume for the ${jobTitle} position` : 
           company ? ` your resume for ${company}` : ' your resume for this job'}
        </p>
      </div>
      
      <div className="prompt-container">
        <div className="prompt-header">
          <div className="prompt-title">
            <FaPen className="prompt-icon" />
            <h3>Custom Instructions</h3>
          </div>
          <button 
            className={`examples-toggle ${showExamples ? 'active' : ''}`}
            onClick={() => setShowExamples(!showExamples)}
          >
            <FaLightbulb className="examples-icon" />
            <span>{showExamples ? 'Hide Examples' : 'Show Examples'}</span>
          </button>
        </div>
        
        <div className="prompt-input-wrap">
          <textarea
            className="prompt-textarea"
            value={customPrompt}
            onChange={handleTextChange}
            placeholder="Add any specific instructions for tailoring your resume. For example: 'Emphasize my project management skills' or 'Focus on my experience with data analysis'..."
            rows={8}
          />
        </div>
        
        {showExamples && (
          <div className="prompt-examples">
            <h4>Example Instructions</h4>
            <p className="examples-description">
              Click on any example below to use it as a starting point for your custom instructions.
            </p>
            
            <div className="examples-grid">
              {promptExamples.map(example => (
                <div 
                  key={example.id} 
                  className="example-card"
                  onClick={() => applyExample(example)}
                >
                  <div className="example-header">
                    <h5>{example.title}</h5>
                    <button className="example-apply-btn">
                      {activatedExample === example.id ? (
                        <><FaCheck className="apply-icon" /> Applied</>
                      ) : (
                        'Apply'
                      )}
                    </button>
                  </div>
                  <p className="example-description">{example.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="prompt-info">
          <FaInfoCircle className="info-icon" />
          <div className="info-content">
            <p>
              Custom instructions help our AI understand what aspects of your background to emphasize.
              You can skip this step if you want a balanced approach to tailoring your resume.
            </p>
          </div>
        </div>
      </div>
      
      <div className="prompt-actions">
        <button onClick={onPrev} className="btn btn-outline">
          <FaArrowLeft /> Back
        </button>
        <button onClick={onNext} className="btn btn-primary">
          <FaMagic /> Continue to Processing
        </button>
      </div>
    </div>
  );
};

CustomPromptInput.propTypes = {
  customPrompt: PropTypes.string,
  jobTitle: PropTypes.string,
  company: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired
};

export default CustomPromptInput;